package com.heelion.farmafreelance.ui

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.heelion.farmafreelance.agent.Agenda
import com.heelion.farmafreelance.agent.AgendaTurni
import com.heelion.farmafreelance.agent.StatoAgenda
import com.heelion.farmafreelance.agent.VoceAgenda
import com.heelion.farmafreelance.models.AnnuncioTurno
import com.heelion.farmafreelance.models.MatchStato
import com.heelion.farmafreelance.models.StoricoEntry
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.math.round

private val VERDE = Color(0xFF2E7D32)
private val ARANCIO = Color(0xFFE65100)

/**
 * "Agenda turni": in un posto solo i turni da fare (confermati, o accettati e in attesa di
 * conferma) e quelli già svolti, mese per mese. Gli stessi dati che Farmy legge per
 * "fammi il riepilogo dei miei turni" (vedi AgendaTurni).
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AgendaScreen(viewModel: AuthViewModel, onBack: () -> Unit) {
    var agenda by remember { mutableStateOf<Agenda?>(null) }
    var storicoMancante by remember { mutableStateOf(false) }
    var passati by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        val storico: List<StoricoEntry>? = viewModel.getStoricoOppureNull()
        storicoMancante = storico == null
        // Orari e compenso: i confermati sono già caricati, gli accettati in attesa si leggono qui.
        val dettagli = viewModel.confirmedShiftsDetails.mapNotNull { a -> a.id?.let { it to a } }.toMap().toMutableMap<String, AnnuncioTurno>()
        viewModel.currentMatches.filter { it.stato == MatchStato.ACCETTATO && it.annuncioId !in dettagli }.forEach { m ->
            viewModel.getAnnuncioByIdSuspend(m.annuncioId)?.let { dettagli[m.annuncioId] = it }
        }
        val oggi = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
        agenda = AgendaTurni.costruisci(viewModel.currentUserRole, viewModel.currentMatches, dettagli, storico.orEmpty(), oggi)
    }

    Scaffold(
        containerColor = Color.Transparent,
        topBar = {
            TopAppBar(
                colors = TopAppBarDefaults.topAppBarColors(containerColor = Color.Transparent),
                title = { Text("Agenda turni", fontWeight = FontWeight.Bold) },
                navigationIcon = { IconButton(onClick = onBack) { Icon(AppIcons.ArrowBack, contentDescription = "Indietro") } }
            )
        }
    ) { padding ->
        Column(Modifier.fillMaxSize().padding(padding).padding(horizontal = 16.dp)) {
            val a = agenda
            if (a == null) {
                Box(Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) { CircularProgressIndicator() }
                return@Column
            }
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                FilterChip(selected = !passati, onClick = { passati = false }, label = { Text("Da fare (${a.daFare.size})") })
                FilterChip(selected = passati, onClick = { passati = true }, label = { Text("Passati (${a.passati.size})") })
            }
            Spacer(Modifier.height(8.dp))
            if (passati && storicoMancante) {
                Text("Non riesco a caricare i turni svolti: controlla la connessione e riprova.", color = MaterialTheme.colorScheme.error)
            }
            val voci = if (passati) a.passati else a.daFare
            if (voci.isEmpty()) {
                Text(
                    if (passati) "Non hai ancora turni svolti." else "Non hai turni in programma. Chiedi a Farmy \"ci sono turni nuovi?\".",
                    modifier = Modifier.padding(vertical = 16.dp)
                )
            }
            // Raggruppati per mese, come il Resoconto pagamenti.
            val mesi = voci.groupBy { it.giorno.year to it.giorno.monthNumber }.toList()
            LazyColumn(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                items(mesi, key = { "${it.first.first}-${it.first.second}" }) { (annoMese, delMese) ->
                    SchedaMeseAgenda(annoMese.first, annoMese.second, delMese)
                }
                item { Spacer(Modifier.height(24.dp)) }
            }
        }
    }
}

@Composable
private fun SchedaMeseAgenda(anno: Int, mese: Int, voci: List<VoceAgenda>) {
    Card(shape = RoundedCornerShape(12.dp), colors = CardDefaults.cardColors(containerColor = Color.White), modifier = Modifier.fillMaxWidth()) {
        Column(Modifier.padding(12.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text("${MESI[mese - 1]} $anno", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, modifier = Modifier.weight(1f))
                val totale = voci.mapNotNull { it.importo }.sum()
                Text(
                    "${voci.size} ${if (voci.size == 1) "turno" else "turni"}" + if (totale > 0) " · ${euro(totale)}" else "",
                    style = MaterialTheme.typography.labelLarge, color = Color.Gray
                )
            }
            voci.forEach { v ->
                HorizontalDivider(Modifier.padding(vertical = 6.dp))
                RigaAgenda(v)
            }
        }
    }
}

@Composable
private fun RigaAgenda(v: VoceAgenda) {
    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
        Column(Modifier.width(52.dp), horizontalAlignment = Alignment.CenterHorizontally) {
            Text(GIORNI[v.giorno.dayOfWeek.ordinal], style = MaterialTheme.typography.labelSmall, color = Color.Gray)
            Text("${v.giorno.dayOfMonth}", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold)
        }
        Column(Modifier.weight(1f)) {
            Text(v.controparte, fontWeight = FontWeight.Bold)
            val dettagli = listOfNotNull(
                if (v.oraInizio != null && v.oraFine != null) "${v.oraInizio}-${v.oraFine}" else null,
                v.luogo,
                v.importo?.let { euro(it) }
            ).joinToString(" · ")
            if (dettagli.isNotEmpty()) Text(dettagli, style = MaterialTheme.typography.bodySmall, color = Color.Gray)
        }
        val (etichetta, colore) = when {
            v.stato == StatoAgenda.CONFERMATO -> "Confermato" to VERDE
            v.stato == StatoAgenda.IN_ATTESA -> "In attesa" to ARANCIO
            v.pagato == true -> "Pagato" to VERDE
            v.pagato == false -> "Da incassare" to ARANCIO
            else -> "Svolto" to Color.Gray
        }
        Text(etichetta, style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold, color = colore)
    }
}

private val MESI = listOf("Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio",
    "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre")
private val GIORNI = listOf("lun", "mar", "mer", "gio", "ven", "sab", "dom")

/** "1.470,50 €" */
private fun euro(v: Double): String {
    val centesimi = round(v * 100).toLong()
    val interi = (centesimi / 100).toString().reversed().chunked(3).joinToString(".").reversed()
    return "$interi,${(centesimi % 100).toString().padStart(2, '0')} €"
}
